import { useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useCallback, useEffect } from "react";
import { FiPlus } from "react-icons/fi";
import {
  CatalogGrid,
  SearchResults,
} from "@/features/catalog/components/lists";
import { SortNav } from "@/features/catalog/components/navigation";
import { useThreads } from "@/features/catalog/hooks";
import { useCatalogStore } from "@/features/catalog/stores";
import { CatalogListView, CatalogToolbar } from "@/features/catalog/ui";
import { ThreadCreateModal } from "@/features/thread/components/modals";
import { useThreadCreateModalStore } from "@/features/thread/stores/threadCreateModalStore";
import { useCurrentBoardSlug } from "@/shared/hooks/useCurrentBoardSlug";
import { useDesktopChrome } from "@/shared/hooks/useDesktopChrome";
import { useDevice } from "@/shared/hooks/useDevice";
import { PullRefresh } from "@/shared/ui/feedback";

export const CatalogPage: React.FunctionComponent = () => {
  const { updateLastCatalogIds, searchQuery } = useCatalogStore();
  const isOpen = useThreadCreateModalStore((s) => s.isOpen);
  const open = useThreadCreateModalStore((s) => s.open);
  const close = useThreadCreateModalStore((s) => s.close);
  const { data, refetch } = useThreads();
  const queryClient = useQueryClient();
  const slug = useCurrentBoardSlug();
  const device = useDevice();

  // ?view query を mobile では強制 catalog (URL は preserve)
  const search = useSearch({ from: "/$boardSlug/" });
  const requestedView = search.view ?? "catalog";
  const effectiveView = device === "mobile" ? "catalog" : requestedView;

  const trimmedQuery = searchQuery.trim();
  const isSearching = trimmedQuery.length > 0;

  // Desktop chrome を slot 注入 (mobile では noop)
  useDesktopChrome({
    toolbar: <CatalogToolbar />,
    pageLabel: "カタログ",
  });

  const onRefresh = useCallback(async () => {
    if (isSearching) {
      // useSearch hook の queryKey: ["search", slug, trimmedQuery]
      await queryClient.invalidateQueries({
        queryKey: ["search", slug, trimmedQuery],
      });
      return;
    }
    await refetch();
  }, [isSearching, queryClient, refetch, slug, trimmedQuery]);

  // カタログページを離れる時に現在のスレッドIDを保存
  useEffect(() => {
    return (): void => {
      if (data?.items) {
        updateLastCatalogIds(data.items.map((t) => t.id));
      }
    };
  }, [data, updateLastCatalogIds]);

  // useThreadCreateModalStore はグローバルな zustand なので、ページ離脱時に
  // close() しないと再訪時に modal が開きっぱなしになる
  useEffect(() => {
    return (): void => {
      close();
    };
  }, [close]);

  const isDesktop = device === "desktop";

  return (
    <>
      <title>{import.meta.env.APP_NAME}</title>
      <PullRefresh onRefresh={onRefresh}>
        {isSearching ? (
          <SearchResults query={trimmedQuery} />
        ) : effectiveView === "list" ? (
          <CatalogListView />
        ) : (
          <CatalogGrid />
        )}
      </PullRefresh>
      {/* mobile では bottom SortNav + FAB を表示。Desktop では CatalogToolbar に集約済 */}
      {!isDesktop && (
        <SortNav
          primaryAction={{
            icon: FiPlus,
            label: "新規スレッド",
            onClick: open,
          }}
        />
      )}
      <ThreadCreateModal isOpen={isOpen} onClose={close} />
    </>
  );
};
